import api from './api';

export interface AIUsageSummary {
  claude: {
    status: string;
    provider: string;
    period: {
      start: string;
      end: string;
      days: number;
    };
    cost?: {
      total_cost_usd: number;
      daily_costs: Array<{
        date: string;
        cost_usd: number;
      }>;
      average_daily_cost: number;
    };
    usage?: {
      total_tokens: number;
      input_tokens: number;
      output_tokens: number;
      cache_creation_tokens: number;
      cache_read_tokens: number;
      daily_usage: Array<{
        date: string;
        input_tokens: number;
        output_tokens: number;
        cache_creation_tokens: number;
        cache_read_tokens: number;
        total_tokens: number;
      }>;
      average_daily_tokens: number;
    };
    message?: string;
  };
  openai: {
    status: string;
    provider: string;
    period: {
      start: string;
      end: string;
      days: number;
    };
    usage?: {
      total_cost_usd: number;
      daily_data: Array<{
        date: string;
        requests: number;
        cost_usd: number;
      }>;
      average_daily_cost: number;
    };
    message?: string;
  };
  summary: {
    total_cost_usd: number;
    active_providers: number;
    period_days: number;
    average_daily_cost: number;
    projected_monthly_cost: number;
  };
}

export interface AIUsageAlert {
  type: string;
  title: string;
  message: string;
  severity: 'low' | 'medium' | 'high';
}

export interface AIUsageConfig {
  default_provider: string;
  claude: {
    api_key_configured: boolean;
    admin_key_configured: boolean;
    usage_tracking_enabled: boolean;
  };
  openai: {
    api_key_configured: boolean;
    usage_tracking_enabled: boolean;
  };
  alert_thresholds: {
    daily_usd: number;
    monthly_usd: number;
  };
}

/**
 * AI Usage Service
 * Handles API calls for AI usage monitoring and cost tracking
 */
class AIUsageService {
  /**
   * Get AI usage summary for all providers
   */
  async getUsageSummary(days: number = 7): Promise<AIUsageSummary> {
    const response = await api.get('/admin/ai-usage/summary', {
      params: { days }
    });
    return response.data.data;
  }

  /**
   * Get Claude usage details
   */
  async getClaudeUsage(days: number = 7) {
    const response = await api.get('/admin/ai-usage/claude', {
      params: { days }
    });
    return response.data.data;
  }

  /**
   * Get OpenAI usage details
   */
  async getOpenAIUsage(days: number = 7) {
    const response = await api.get('/admin/ai-usage/openai', {
      params: { days }
    });
    return response.data.data;
  }

  /**
   * Get usage alerts
   */
  async getAlerts(): Promise<AIUsageAlert[]> {
    const response = await api.get('/admin/ai-usage/alerts');
    return response.data.data.alerts;
  }

  /**
   * Get current AI configuration
   */
  async getConfiguration(): Promise<AIUsageConfig> {
    const response = await api.get('/admin/ai-usage/config');
    return response.data.data;
  }

  /**
   * Update alert thresholds
   */
  async updateThresholds(dailyUsd: number, monthlyUsd: number) {
    const response = await api.put('/admin/ai-usage/thresholds', {
      daily_usd: dailyUsd,
      monthly_usd: monthlyUsd
    });
    return response.data;
  }

  /**
   * Configure Claude Admin API key
   */
  async configureAdminKey(provider: string, adminKey: string) {
    const response = await api.post('/admin/ai-usage/configure-admin-key', {
      provider,
      admin_key: adminKey
    });
    return response.data;
  }

  /**
   * Clear usage cache
   */
  async clearCache() {
    const response = await api.post('/admin/ai-usage/clear-cache');
    return response.data;
  }
}

export default new AIUsageService();
